import type { AISessionSseEvent } from '@/types/ai-session';
import {
  getStageEventBooleanField,
  getStageEventStringField,
  readStageEventField,
} from '@/lib/ai-session/contracts/utils';
import type {
  AgentProgressAgent,
  AgentProgressEvent,
  AgentProgressPhase,
  AgentProgressRunKind,
  AgentProgressRunMode,
  AgentProgressStatus,
  AgentProgressSubStep,
  AgentProgressSubStepStatus,
  AgentProgressTask,
} from '@/lib/ai-session/presentation/agent-progress-types';

export type ClarificationQuestionType = 'single' | 'multi' | 'text';

/** 服务端 `questions[].options[]` 的前端投影（change tool-first-plain-text-terminal）。 */
export interface ClarificationOption {
  id: string;
  label: string;
  recommended?: boolean;
  /** 服务端合成选项：点选后作为用户消息正文发送；模型出题的选项无此字段，回填用 label。 */
  content?: string;
  /** alignment 合成选项：consult | revise | supplement | accept */
  action?: 'consult' | 'revise' | 'supplement' | 'accept' | string;
  /** ADR-011 P2：目标章节的稳定 block.id；定位经此字段，不经 content 文本。 */
  sectionId?: string;
  /** 目标章节展示标题（配合 sectionId，仅用于展示/兜底）。 */
  sectionTitle?: string;
}

export interface ClarificationQuestion {
  id: string;
  prompt: string;
  type: ClarificationQuestionType;
  /** single / multi 至少一项；text 题无选项。 */
  options?: ClarificationOption[];
}

/** 提交给服务端的结构化回答：single / text → string，multi → string[]。 */
export type ClarificationAnswers = Record<string, string | string[]>;

export interface StageClarification {
  stage: 'alignment' | 'asset' | 'storyboard';
  /** 可选开场说明（为什么要问）。 */
  preface?: string;
  questions: ClarificationQuestion[];
  /** 单一确认 CTA（非多选消歧）；用于默认策略 pendingOffer */
  kind?: 'pending_offer_apply';
  /** 「按此建议修改」跑出来的澄清卡：点选改稿项时续传，避免下一轮丢掉改稿意图。 */
  executeAsEdit?: boolean;
  sourceMessageId?: string;
}

const CLARIFICATION_QUESTION_TYPES = new Set<ClarificationQuestionType>([
  'single',
  'multi',
  'text',
]);

function readTrimmedString(record: Record<string, unknown>, key: string): string {
  const value = record[key];
  return typeof value === 'string' ? value.trim() : '';
}

function parseClarificationOption(value: unknown): ClarificationOption | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  const id = readTrimmedString(record, 'id');
  const label = readTrimmedString(record, 'label');
  if (!id || !label) return null;
  const content = readTrimmedString(record, 'content');
  const action = readTrimmedString(record, 'action');
  const sectionId = readTrimmedString(record, 'sectionId');
  const sectionTitle = readTrimmedString(record, 'sectionTitle');
  return {
    id,
    label,
    ...(record.recommended === true ? { recommended: true } : {}),
    ...(content ? { content } : {}),
    ...(action ? { action } : {}),
    ...(sectionId ? { sectionId } : {}),
    ...(sectionTitle ? { sectionTitle } : {}),
  };
}

function parseClarificationQuestion(value: unknown): ClarificationQuestion | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  const id = readTrimmedString(record, 'id');
  const prompt = readTrimmedString(record, 'prompt');
  if (!id || !prompt) return null;
  const rawOptions = Array.isArray(record.options) ? record.options : [];
  const seen = new Set<string>();
  const options = rawOptions
    .map(parseClarificationOption)
    .filter((option): option is ClarificationOption => {
      if (!option || seen.has(option.id)) return false;
      seen.add(option.id);
      return true;
    });
  const rawType = readTrimmedString(record, 'type');
  const type: ClarificationQuestionType = CLARIFICATION_QUESTION_TYPES.has(
    rawType as ClarificationQuestionType
  )
    ? (rawType as ClarificationQuestionType)
    : options.length > 0
      ? 'single'
      : 'text';
  if (type === 'text') return { id, prompt, type };
  if (options.length === 0) return null;
  return {
    id,
    prompt,
    type,
    options:
      type === 'multi'
        ? options.map((option) => {
            const rest: ClarificationOption = {
              id: option.id,
              label: option.label,
              ...(option.content ? { content: option.content } : {}),
              ...(option.action ? { action: option.action } : {}),
              ...(option.sectionId ? { sectionId: option.sectionId } : {}),
              ...(option.sectionTitle ? { sectionTitle: option.sectionTitle } : {}),
            };
            return rest;
          })
        : options,
  };
}

/** 解析服务端 `questions[]`；去掉无效题 / 重复 id，无有效题返回空数组。 */
export function parseClarificationQuestions(value: unknown): ClarificationQuestion[] {
  if (!Array.isArray(value)) return [];
  const seen = new Set<string>();
  return value
    .map(parseClarificationQuestion)
    .filter((question): question is ClarificationQuestion => {
      if (!question || seen.has(question.id)) return false;
      seen.add(question.id);
      return true;
    });
}

export function readClarificationPreface(
  record: Record<string, unknown> | null | undefined
): Pick<StageClarification, 'preface'> {
  const preface = record ? readTrimmedString(record, 'preface') : '';
  return preface ? { preface } : {};
}

export function readExecuteAsEditFollowThrough(
  record: Record<string, unknown> | null | undefined
): Pick<StageClarification, 'executeAsEdit' | 'sourceMessageId'> {
  if (!record || record.executeAsEdit !== true) return {};
  const sourceMessageId =
    typeof record.sourceMessageId === 'string' ? record.sourceMessageId.trim() : '';
  return {
    executeAsEdit: true,
    ...(sourceMessageId ? { sourceMessageId } : {}),
  };
}

const AGENT_PROGRESS_PHASES = new Set<AgentProgressPhase>([
  'alignment',
  'asset_breakdown',
  'storyboard_breakdown',
]);

const AGENT_PROGRESS_AGENTS = new Set<AgentProgressAgent>([
  'coordinator',
  'reading-agent',
  'scene-agent',
  'suggestion-agent',
  'alignment-editor-agent',
  'asset-agent',
  'asset-detail-agent',
  'asset-detail-merge-agent',
  'asset-editor-agent',
  'merge-agent',
  'storyboard-agent',
  'storyboard-editor-agent',
  'review-agent',
]);

const AGENT_PROGRESS_STATUSES = new Set<AgentProgressStatus>([
  'waiting',
  'running',
  'completed',
  'failed',
  'requires_action',
]);

export function isConfirmationInteraction(event: AISessionSseEvent): boolean {
  return getStageEventStringField(event, 'interactionType') === 'confirmation';
}

const AGENT_PROGRESS_SUB_STEP_STATUSES = new Set<AgentProgressSubStepStatus>([
  'waiting',
  'running',
  'completed',
  'failed',
]);

const AGENT_PROGRESS_RUN_MODES = new Set<AgentProgressRunMode>(['checklist', 'detail']);

const AGENT_PROGRESS_RUN_KINDS = new Set<AgentProgressRunKind>(['generate', 'follow_up']);

function parseAgentProgressTask(value: unknown): AgentProgressTask | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;
  const id = typeof record.id === 'string' ? record.id.trim() : '';
  const agent = typeof record.agent === 'string' ? record.agent : '';
  const label = typeof record.label === 'string' ? record.label.trim() : '';
  const status = typeof record.status === 'string' ? record.status : '';
  if (
    !id ||
    !label ||
    !AGENT_PROGRESS_AGENTS.has(agent as AgentProgressAgent) ||
    !AGENT_PROGRESS_STATUSES.has(status as AgentProgressStatus)
  ) {
    return null;
  }
  return {
    id,
    agent: agent as AgentProgressAgent,
    label,
    status: status as AgentProgressStatus,
    ...(typeof record.blocking === 'boolean' ? { blocking: record.blocking } : {}),
    ...(typeof record.progressText === 'string' && record.progressText.trim()
      ? { progressText: record.progressText.trim() }
      : {}),
    ...(typeof record.result === 'string' && record.result.trim()
      ? { result: record.result.trim() }
      : {}),
    ...parseTaskSubStepFields(record),
    ...parseTaskCompletion(record.completion),
  };
}

function parseTaskCompletion(value: unknown): Pick<AgentProgressTask, 'completion'> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const { completed, total } = value as Record<string, unknown>;
  if (
    typeof completed !== 'number' ||
    !Number.isInteger(completed) ||
    completed < 0 ||
    typeof total !== 'number' ||
    !Number.isInteger(total) ||
    total <= 0 ||
    completed > total
  )
    return {};
  return { completion: { completed, total } };
}

function parseTaskSubStepFields(
  record: Record<string, unknown>
): Pick<AgentProgressTask, 'subSteps' | 'subStepsCollapsed'> {
  const subSteps = parseAgentProgressSubSteps(record.subSteps);
  if (!subSteps?.length) return {};
  return {
    subSteps,
    ...(record.subStepsCollapsed === true ? { subStepsCollapsed: true } : {}),
  };
}

export function parseAgentProgressSubSteps(value: unknown): AgentProgressSubStep[] | undefined {
  if (!Array.isArray(value)) return undefined;
  return value
    .filter((item): item is Record<string, unknown> =>
      Boolean(item && typeof item === 'object' && !Array.isArray(item))
    )
    .filter(
      (item) =>
        typeof item.id === 'string' &&
        typeof item.title === 'string' &&
        AGENT_PROGRESS_SUB_STEP_STATUSES.has(item.status as AgentProgressSubStepStatus)
    )
    .map((item) => ({
      id: item.id as string,
      title: (item.title as string).trim(),
      status: item.status as AgentProgressSubStepStatus,
      ...(typeof item.summary === 'string' && item.summary.trim()
        ? { summary: item.summary.trim() }
        : {}),
    }));
}

/** 旧协议「读取场次」场次清单行；只在解析时归一为 scene 任务的已完成子步骤。 */
type AlignmentReadingScopeRow = { id: string; title: string; meta: string };

const LEGACY_SUB_STEP_TASK_IDS = {
  suggestion: 'suggestion',
  strategy: 'merge',
  scene: 'scene',
} as const;

/** 旧协议把子步骤放在事件顶层；统一挂到对应任务，消费方只读 task.subSteps。 */
function attachLegacySubSteps(
  tasks: AgentProgressTask[],
  legacy: {
    suggestionOutline?: AgentProgressSubStep[];
    strategyOutline?: AgentProgressSubStep[];
    readingScope: AlignmentReadingScopeRow[];
  }
): AgentProgressTask[] {
  const sceneSteps: AgentProgressSubStep[] = legacy.readingScope.map((row) => ({
    id: row.id,
    title: row.title,
    status: 'completed',
    ...(row.meta ? { summary: row.meta } : {}),
  }));
  const byTaskId: Record<string, { steps?: AgentProgressSubStep[]; collapsed?: boolean }> = {
    [LEGACY_SUB_STEP_TASK_IDS.suggestion]: { steps: legacy.suggestionOutline },
    [LEGACY_SUB_STEP_TASK_IDS.strategy]: { steps: legacy.strategyOutline },
    [LEGACY_SUB_STEP_TASK_IDS.scene]: { steps: sceneSteps, collapsed: true },
  };
  return tasks.map((task) => {
    const legacySteps = byTaskId[task.id];
    if (task.subSteps?.length || !legacySteps?.steps?.length) return task;
    return {
      ...task,
      subSteps: legacySteps.steps,
      ...(legacySteps.collapsed ? { subStepsCollapsed: true } : {}),
    };
  });
}

function parseAlignmentReadingScope(value: unknown): AlignmentReadingScopeRow[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return [];
    const row = item as Record<string, unknown>;
    if (
      typeof row.id !== 'string' ||
      typeof row.title !== 'string' ||
      typeof row.meta !== 'string'
    ) {
      return [];
    }
    const title = row.title.trim();
    return title ? [{ id: row.id, title, meta: row.meta.trim() }] : [];
  });
}

function parseAgentProgressRecord(record: Record<string, unknown>): AgentProgressEvent | undefined {
  if (record.type !== 'agent_progress') return undefined;
  const phase = record.phase;
  const title = record.title;
  const rawTasks = record.tasks;
  if (
    typeof phase !== 'string' ||
    !AGENT_PROGRESS_PHASES.has(phase as AgentProgressPhase) ||
    typeof title !== 'string' ||
    !title.trim() ||
    !Array.isArray(rawTasks)
  ) {
    return undefined;
  }
  const parsedTasks = rawTasks
    .map(parseAgentProgressTask)
    .filter((task): task is AgentProgressTask => Boolean(task));
  if (parsedTasks.length === 0) return undefined;
  const summary = record.summary;
  const stage = record.stage;
  const previewReady = record.previewReady;
  const phaseLabel = record.phaseLabel;
  const runMode = record.runMode;
  const runKind = record.runKind;
  const displayTitle = readTrimmedString(record, 'displayTitle');
  const countKeys =
    phase === 'asset_breakdown'
      ? (['characters', 'locations', 'props'] as const)
      : phase === 'storyboard_breakdown'
        ? (['storyboards'] as const)
        : [];
  const parseCounts = (rawCounts: unknown): AgentProgressEvent['resultCounts'] => {
    const counts =
      rawCounts && typeof rawCounts === 'object' && !Array.isArray(rawCounts)
        ? (rawCounts as Record<string, unknown>)
        : null;
    return counts &&
      countKeys.length > 0 &&
      countKeys.every(
        (key) =>
          typeof counts[key] === 'number' && Number.isSafeInteger(counts[key]) && counts[key] >= 0
      )
      ? (Object.fromEntries(
          countKeys.map((key) => [key, counts[key]])
        ) as AgentProgressEvent['resultCounts'])
      : undefined;
  };
  const resultCounts = parseCounts(record.resultCounts);
  const partialCounts = parseCounts(record.partialCounts);
  const suggestionOutline = parseAgentProgressSubSteps(record.suggestionOutline);
  const strategyOutline = parseAgentProgressSubSteps(record.strategyOutline);
  const readingScope = parseAlignmentReadingScope(record.readingScope);
  const tasks = attachLegacySubSteps(parsedTasks, {
    suggestionOutline,
    strategyOutline,
    readingScope,
  });
  return {
    type: 'agent_progress',
    phase: phase as AgentProgressPhase,
    ...(typeof phaseLabel === 'string' && phaseLabel.trim()
      ? { phaseLabel: phaseLabel.trim() }
      : {}),
    ...(AGENT_PROGRESS_RUN_MODES.has(runMode as AgentProgressRunMode)
      ? { runMode: runMode as AgentProgressRunMode }
      : {}),
    ...(AGENT_PROGRESS_RUN_KINDS.has(runKind as AgentProgressRunKind)
      ? { runKind: runKind as AgentProgressRunKind }
      : {}),
    title: title.trim(),
    ...(displayTitle ? { displayTitle } : {}),
    ...(resultCounts ? { resultCounts } : {}),
    ...(partialCounts ? { partialCounts } : {}),
    ...(typeof stage === 'string' && stage.trim() ? { stage: stage.trim() } : {}),
    tasks,
    ...(typeof summary === 'string' && summary.trim() ? { summary: summary.trim() } : {}),
    ...(typeof previewReady === 'boolean' ? { previewReady } : {}),
    ...(typeof record.revision === 'number' && Number.isFinite(record.revision)
      ? { revision: record.revision }
      : {}),
  };
}

export function parseAgentProgressEvent(event: AISessionSseEvent): AgentProgressEvent | undefined {
  const record: Record<string, unknown> = {};
  for (const key of [
    'type',
    'phase',
    'phaseLabel',
    'runMode',
    'runKind',
    'title',
    'displayTitle',
    'resultCounts',
    'partialCounts',
    'tasks',
    'summary',
    'stage',
    'previewReady',
    'suggestionOutline',
    'strategyOutline',
    'readingScope',
    'revision',
  ]) {
    record[key] = readStageEventField(event, key);
  }
  return parseAgentProgressRecord(record);
}

export function parseAgentProgressPayload(value: unknown): AgentProgressEvent | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  return parseAgentProgressRecord(value as Record<string, unknown>);
}

/** 会话快照 / 跨实例存储里的剧本分析进度：解析并把旧顶层字段归一到任务子步骤。 */
export function parseAlignmentAgentProgress(value: unknown): AgentProgressEvent | null {
  const progress = parseAgentProgressPayload(value);
  return progress?.phase === 'alignment' ? progress : null;
}

/**
 * 从 SSE `interaction_required` 读取结构化澄清；只认 `questions[]`，
 * 旧 `question` / `clarificationOptions` 不再解析（服务端已在 wire 层收敛）。
 */
export function parseStageClarification(
  event: AISessionSseEvent,
  stage: StageClarification['stage']
): StageClarification | undefined {
  const questions = parseClarificationQuestions(readStageEventField(event, 'questions'));
  if (questions.length === 0) return undefined;
  const preface = getStageEventStringField(event, 'preface')?.trim();
  return {
    stage,
    ...(preface ? { preface } : {}),
    questions,
    ...readExecuteAsEditFollowThrough({
      executeAsEdit: getStageEventBooleanField(event, 'executeAsEdit'),
      sourceMessageId: getStageEventStringField(event, 'sourceMessageId'),
    }),
  };
}

export function getRecordData(
  event: AISessionSseEvent,
  key: string
): Record<string, unknown> | undefined {
  const value = readStageEventField(event, key);
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}
